import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, FilePlus2, History, Pencil, Plus, ShieldCheck, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
  type BadgeTone,
} from '../components/ui';
import { AttachedFiles, EvidencePicker, HISTORICAL_EVIDENCE, useEvidenceUpload, type AttachedFile } from '../components/EvidenceFiles';
import { useRefusal } from './PartnerForm';

/**
 * Historical finance (Finance Phase 1, 2026-10-28): one total a month of
 * salaries, or of other expenses, paid before the system held them — with the
 * Excel or PDF it was taken from. A past cost for the P&L and its trends, and
 * nothing else: no payroll, no expense rows, no movement of today's money.
 */

export type RecordType = 'HISTORICAL_SALARIES' | 'HISTORICAL_EXPENSES';
export type RecordStatus = 'DRAFT' | 'FINALIZED' | 'VOIDED';

export interface OverlapFinding {
  kind: 'PAYROLL_PERIOD' | 'SALARY_EXPENSES' | 'EXPENSES' | 'OTHER_CURRENCY_RECORD' | 'AFTER_CUTOVER';
  currency?: string;
  amount?: string;
  count?: number;
  status?: string;
  recordId?: string;
  cutoverDate?: string;
}

export interface HistoricalRecord {
  id: string;
  type: RecordType;
  periodMonth: string;
  totalAmount: string;
  currency: string;
  exchangeRate: string | null;
  baseCurrency: string | null;
  baseAmount: string | null;
  status: RecordStatus;
  notes: string | null;
  source: string | null;
  externalReference: string | null;
  revision: number;
  paymentStatus: 'PAID';
  missingDocument: boolean;
  attachments: AttachedFile[];
  removedAttachments: Array<{ id: string; removedAt: string; name: string }>;
  revisions: Array<{ id: string; action: string; before: any; after: any; reason: string | null; createdAt: string }>;
  overlapReview: { reviewedAt: string; findings: OverlapFinding[]; decision: string | null; note: string | null; cutoverDate: string | null } | null;
  finalizedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  baseCurrencyNow: string;
  cutover: { cutoverDate: string; status: string } | null;
  reachesCutover: boolean;
  overlap: OverlapFinding[];
}

/** "2025-01-01" → "January 2025" / "يناير 2025". */
export function formatMonth(month: string | null | undefined, locale: Locale): string {
  if (!month) return '—';
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${month.slice(0, 7)}-01T00:00:00Z`),
  );
}

export const STATUS_TONE: Record<RecordStatus, BadgeTone> = { DRAFT: 'warning', FINALIZED: 'success', VOIDED: 'neutral' };

export function StatusBadge({ status }: { status: RecordStatus }) {
  const { t } = useI18n();
  return <Badge tone={STATUS_TONE[status]}>{t(`historical.status.${status}` as TranslationKey)}</Badge>;
}

/** What a finding says, in the reader's language. */
export function FindingText({ finding }: { finding: OverlapFinding }) {
  const { t, locale } = useI18n();
  const amount = finding.amount && finding.currency ? formatMoney(finding.amount, finding.currency, locale) : '';
  switch (finding.kind) {
    case 'PAYROLL_PERIOD':
      return <>{t('historical.finding.PAYROLL_PERIOD', { status: enumLabel(finding.status, locale), amount, count: finding.count ?? 0 })}</>;
    case 'SALARY_EXPENSES':
    case 'EXPENSES':
      return <>{t(`historical.finding.${finding.kind}`, { amount, count: finding.count ?? 0 })}</>;
    case 'OTHER_CURRENCY_RECORD':
      return (
        <>
          {t('historical.finding.OTHER_CURRENCY_RECORD', {
            currency: finding.currency ?? '',
            amount,
            status: t(`historical.status.${finding.status ?? 'DRAFT'}` as TranslationKey),
          })}
        </>
      );
    case 'AFTER_CUTOVER':
      return <>{t('historical.finding.AFTER_CUTOVER', { date: formatDate(finding.cutoverDate, locale) })}</>;
  }
}

// ---------------------------------------------------------------------------
// The page: the monthly summary, and each type's list
// ---------------------------------------------------------------------------

/** Route: `/historical` (`?tab=summary|salaries|expenses`). */
export function HistoricalFinancePage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'summary';

  return (
    <>
      <PageHeader
        title={t('historical.title')}
        subtitle={t('historical.subtitle')}
        action={
          can(P.HISTORICAL_FINANCE_MANAGE) && (
            <>
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/historical/new?type=HISTORICAL_EXPENSES')}>
                {t('historical.newExpenses')}
              </Button>
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/historical/new?type=HISTORICAL_SALARIES')}>
                {t('historical.newSalaries')}
              </Button>
            </>
          )
        }
      />
      <Tabs
        active={tab}
        onChange={(id) => setParams({ tab: id }, { replace: true })}
        tabs={[
          { id: 'summary', label: t('historical.summary') },
          { id: 'salaries', label: t('historical.salaries') },
          { id: 'expenses', label: t('historical.expenses') },
        ]}
      />
      <div className="mt-5">
        {tab === 'summary' ? (
          <MonthlySummary />
        ) : (
          <RecordList type={tab === 'salaries' ? 'HISTORICAL_SALARIES' : 'HISTORICAL_EXPENSES'} />
        )}
      </div>
    </>
  );
}

interface SummaryCell {
  recordId: string;
  currency: string;
  amount: string;
  baseAmount: string | null;
  status: RecordStatus;
  attachments: number;
}

interface Summary {
  from: string;
  to: string;
  baseCurrency: string;
  cutoverDate: string | null;
  rows: Array<{
    month: string;
    salaries: SummaryCell[];
    expenses: SummaryCell[];
    salariesBase: string | null;
    expensesBase: string | null;
    totalBase: string | null;
    covered: { salaries: boolean; expenses: boolean };
    unconverted: boolean;
    drafts: number;
    attachments: number;
    missingDocuments: number;
  }>;
  totals: {
    salariesBase: string;
    expensesBase: string;
    totalBase: string;
    months: number;
    missingSalaryMonths: number;
    missingExpenseMonths: number;
    unconvertedMonths: number;
  };
}

function MonthlySummary() {
  const { t, locale } = useI18n();
  const [range, setRange] = useState({ from: '', to: '', currency: '' });
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['historical', 'summary', range],
    queryFn: () => api.get<Summary>('/historical-records/summary', range),
  });
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Array<{ code: string }>>('/currencies') }).data;

  // A month with no record is unknown spend, never zero; a draft is listed and not counted.
  const cells = (list: SummaryCell[]) =>
    list.length === 0 ? (
      <span className="text-caption text-faint">{t('historical.notCovered')}</span>
    ) : (
      <span className="flex flex-col items-end gap-0.5">
        {list.map((c) => (
          <Link key={c.recordId} to={`/historical/${c.recordId}`} className="tabular hover:text-primary">
            {formatMoney(c.amount, c.currency, locale)}
            {c.status === 'DRAFT' && <span className="ms-1.5 text-caption text-warning-fg dark:text-warning-dark">· {t('historical.draftNotCounted')}</span>}
          </Link>
        ))}
      </span>
    );

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Input type="month" aria-label={t('common.from')} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          <Input type="month" aria-label={t('common.to')} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          <Select value={range.currency} onChange={(e) => setRange({ ...range, currency: e.target.value })}>
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {currencies?.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
        </div>
      </Card>

      {data && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Metric label={t('historical.salariesCol')} value={formatMoney(data.totals.salariesBase, data.baseCurrency, locale)} hint={t('historical.inBase', { base: data.baseCurrency })} />
          <Metric label={t('historical.expensesCol')} value={formatMoney(data.totals.expensesBase, data.baseCurrency, locale)} hint={t('historical.inBase', { base: data.baseCurrency })} />
          <Metric
            label={t('historical.totalCol')}
            value={formatMoney(data.totals.totalBase, data.baseCurrency, locale)}
            tone={data.totals.missingSalaryMonths || data.totals.missingExpenseMonths ? 'warning' : 'neutral'}
            hint={t('historical.monthsMissing', { salaries: data.totals.missingSalaryMonths, expenses: data.totals.missingExpenseMonths })}
          />
        </div>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.rows.length ? (
          <EmptyState icon={<History className="h-8 w-8" />} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('historical.month')}</Th>
                <Th align="end">{t('historical.salariesCol')}</Th>
                <Th align="end">{t('historical.expensesCol')}</Th>
                <Th align="end">{t('historical.totalCol')} ({data.baseCurrency})</Th>
                <Th align="end">{t('historical.documents')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <Tr key={row.month}>
                  <Td className="whitespace-nowrap">{formatMonth(row.month, locale)}</Td>
                  <Td align="end">{cells(row.salaries)}</Td>
                  <Td align="end">{cells(row.expenses)}</Td>
                  <Td align="end" className="tabular">
                    {row.totalBase !== null ? (
                      formatMoney(row.totalBase, data.baseCurrency, locale)
                    ) : row.unconverted ? (
                      <span className="text-caption text-warning-fg dark:text-warning-dark">{t('historical.unconverted', { base: data.baseCurrency })}</span>
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </Td>
                  <Td align="end" className="tabular">
                    {row.attachments}
                    {row.missingDocuments > 0 && (
                      <Badge tone="warning" className="ms-2">{t('historical.missingDocument')}</Badge>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function RecordList({ type }: { type: RecordType }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const set = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['historical', 'list', type, page, filters],
    queryFn: () => api.get<Paginated<HistoricalRecord & { attachments: number }>>('/historical-records', { type, page, pageSize: 25, ...filters }),
  });
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Array<{ code: string }>>('/currencies') }).data;

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Select value={filters.status ?? ''} onChange={(e) => set('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {(['DRAFT', 'FINALIZED', 'VOIDED'] as const).map((s) => (
              <option key={s} value={s}>{t(`historical.status.${s}` as TranslationKey)}</option>
            ))}
          </Select>
          <Select value={filters.currency ?? ''} onChange={(e) => set('currency', e.target.value)}>
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {currencies?.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <Input type="month" aria-label={t('common.from')} value={filters.from ?? ''} onChange={(e) => set('from', e.target.value)} />
          <Input type="month" aria-label={t('common.to')} value={filters.to ?? ''} onChange={(e) => set('to', e.target.value)} />
          <Select value={filters.hasDocuments ?? ''} onChange={(e) => set('hasDocuments', e.target.value)}>
            <option value="">{t('historical.documents')} — {t('common.all')}</option>
            <option value="true">{t('historical.withDocuments')}</option>
            <option value="false">{t('historical.withoutDocuments')}</option>
          </Select>
        </div>
      </Card>
      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<History className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('historical.month')}</Th>
                  <Th align="end">{t('historical.total')}</Th>
                  <Th align="end">{t('historical.base', { base: data.items[0]?.baseCurrency ?? '' })}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('historical.documents')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/historical/${r.id}`)}>
                    <Td className="whitespace-nowrap">{formatMonth(r.periodMonth, locale)}</Td>
                    <Td align="end" className="tabular">{formatMoney(r.totalAmount, r.currency, locale)}</Td>
                    <Td align="end" className="tabular text-muted">{r.baseAmount && r.baseCurrency ? formatMoney(r.baseAmount, r.baseCurrency, locale) : '—'}</Td>
                    <Td><StatusBadge status={r.status} /></Td>
                    <Td align="end" className="tabular">
                      {r.attachments === 0 ? <Badge tone="warning">{t('historical.missingDocument')}</Badge> : r.attachments}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// One month
// ---------------------------------------------------------------------------

/** Route: `/historical/:id`. */
export function HistoricalRecordPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refusal = useRefusal('historical');
  const upload = useEvidenceUpload(HISTORICAL_EVIDENCE);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['historical', id],
    queryFn: () => api.get<HistoricalRecord>(`/historical-records/${id}`),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['historical'] });
  };

  const addFiles = useMutation({
    mutationFn: () => api.post(`/historical-records/${id}/attachments`, { fileIds: upload.ids }),
    onSuccess: () => {
      upload.clear();
      refresh();
    },
    onError: (e) => toast.error(refusal(e)),
  });
  const [removing, setRemoving] = useState<string | null>(null);
  const removeFile = useMutation({
    mutationFn: (attachmentId: string) => api.del(`/historical-records/${id}/attachments/${attachmentId}`),
    onMutate: (attachmentId) => setRemoving(attachmentId),
    onSettled: () => setRemoving(null),
    onSuccess: refresh,
    onError: (e) => toast.error(refusal(e)),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const month = formatMonth(data.periodMonth, locale);
  const manage = can(P.HISTORICAL_FINANCE_MANAGE);
  const correct = can(P.HISTORICAL_FINANCE_CORRECT);
  const listTab = data.type === 'HISTORICAL_SALARIES' ? 'salaries' : 'expenses';

  return (
    <>
      <div className="mb-4">
        <Link to={`/historical?tab=${listTab}`} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t(`historical.${listTab}` as TranslationKey)}
        </Link>
      </div>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {month}
            <StatusBadge status={data.status} />
            {data.missingDocument && data.status !== 'VOIDED' && <Badge tone="warning">{t('historical.missingDocument')}</Badge>}
          </span>
        }
        subtitle={t(`historical.${data.type}` as TranslationKey)}
        action={
          <>
            {manage && data.status === 'DRAFT' && (
              <>
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/historical/${data.id}/edit`)}>
                  {t('common.edit')}
                </Button>
                <Button variant="primary" icon={<CheckCircle2 className="h-4 w-4" />} onClick={() => navigate(`/historical/${data.id}/finalize`)}>
                  {t('historical.finalize')}
                </Button>
              </>
            )}
            {correct && data.status === 'FINALIZED' && (
              <Button icon={<Wrench className="h-4 w-4" />} onClick={() => navigate(`/historical/${data.id}/correct`)}>
                {t('historical.correct')}
              </Button>
            )}
            {data.status !== 'VOIDED' && (data.status === 'DRAFT' ? manage : correct) && (
              <Button variant="ghost" icon={<Ban className="h-4 w-4" />} onClick={() => navigate(`/historical/${data.id}/void`)}>
                {t('historical.void')}
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Metric label={t('historical.total')} value={formatMoney(data.totalAmount, data.currency, locale)} hint={t('historical.paid')} />
        <Metric
          label={t('historical.base', { base: data.baseCurrency ?? data.baseCurrencyNow })}
          value={data.baseAmount ? formatMoney(data.baseAmount, data.baseCurrency ?? data.baseCurrencyNow, locale) : '—'}
          hint={data.exchangeRate && data.currency !== (data.baseCurrency ?? data.baseCurrencyNow) ? `1 ${data.currency} = ${data.exchangeRate} ${data.baseCurrency ?? data.baseCurrencyNow}` : undefined}
        />
        <Metric label={t('historical.documents')} value={data.attachments.length} tone={data.missingDocument ? 'warning' : 'neutral'} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('historical.files')} subtitle={t('historical.filesHint')} />
          <AttachedFiles files={data.attachments} onRemove={manage && data.status !== 'VOIDED' ? (aid) => removeFile.mutate(aid) : undefined} removing={removing} />
          {manage && data.status !== 'VOIDED' && (
            <div className="mt-4">
              <EvidencePicker upload={upload} policy={HISTORICAL_EVIDENCE} />
              {upload.files.length > 0 && (
                <Button className="mt-3" variant="primary" size="sm" icon={<FilePlus2 className="h-4 w-4" />} loading={addFiles.isPending} disabled={upload.busy} onClick={() => addFiles.mutate()}>
                  {t('common.add')}
                </Button>
              )}
            </div>
          )}
          {data.removedAttachments.length > 0 && (
            <div className="mt-4 border-t hairline pt-3">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('historical.removedFiles')}</p>
              <ul className="space-y-0.5 text-caption text-muted">
                {data.removedAttachments.map((a) => (
                  <li key={a.id} dir="auto">
                    {a.name} · {formatDate(a.removedAt, locale)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <dl>
              <Detail label={t('historical.type')}>{t(`historical.${data.type}` as TranslationKey)}</Detail>
              <Detail label={t('historical.month')}>{month}</Detail>
              <Detail label={t('common.currency')}>{data.currency}</Detail>
              <Detail label={t('historical.paid')}>{t('historical.paidHint')}</Detail>
              {data.source && <Detail label={t('historical.source')}><span dir="auto">{data.source}</span></Detail>}
              {data.externalReference && <Detail label={t('historical.reference')}><span dir="auto">{data.externalReference}</span></Detail>}
              {data.voidReason && <Detail label={t('common.reason')}><span dir="auto">{data.voidReason}</span></Detail>}
            </dl>
            {data.notes && <p className="mt-3 whitespace-pre-line text-body-sm text-muted" dir="auto">{data.notes}</p>}
          </Card>

          {data.status !== 'VOIDED' && (
            <Card>
              <CardHeader title={t('historical.overlap')} />
              {data.overlap.length === 0 ? (
                <p className="text-body-sm text-muted">{t('historical.overlapNone')}</p>
              ) : (
                <ul className="list-disc space-y-1 ps-5 text-body-sm">
                  {data.overlap.map((f, i) => (
                    <li key={i}><FindingText finding={f} /></li>
                  ))}
                </ul>
              )}
              {data.overlapReview && (
                <div className="mt-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2 text-body-sm">
                  <p className="flex items-center gap-1.5 font-medium">
                    <ShieldCheck className="h-4 w-4 text-success-fg dark:text-success" aria-hidden />
                    {t('historical.reviewedOn', { date: formatDateTime(data.overlapReview.reviewedAt, locale) })}
                  </p>
                  {data.overlapReview.note && <p className="mt-1 text-muted" dir="auto">{data.overlapReview.note}</p>}
                </div>
              )}
            </Card>
          )}

          {data.revisions.length > 0 && (
            <Card>
              <CardHeader title={t('historical.revisions')} />
              <ul className="space-y-2 text-body-sm">
                {data.revisions.map((r) => (
                  <li key={r.id}>
                    <span className="font-medium">{t(`historical.revision.${r.action}` as TranslationKey)}</span> ·{' '}
                    <span className="text-muted">{formatDateTime(r.createdAt, locale)}</span>
                    {r.before?.totalAmount && r.after?.totalAmount && r.before.totalAmount !== r.after.totalAmount && (
                      <span className="block tabular text-muted">
                        {formatMoney(r.before.totalAmount, r.before.currency, locale)} → {formatMoney(r.after.totalAmount, r.after.currency, locale)}
                      </span>
                    )}
                    {r.reason && <span className="block text-muted" dir="auto">{r.reason}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
